import { RestResponseFactory } from '@/models/apis/RestResponse'
import { StatusCodes } from '@/models/apis/StatusCodes'
import { CreateFacebookConfigInput, UpdateFacebookConfigInput } from '@/modules/configuration/domain/models/Channels/FacebookConfig'
import { CreateInstagramConfigInput, UpdateInstagramConfigInput } from '@/modules/configuration/domain/models/Channels/InstagramConfig'
import store from '@/store'
import axios from 'axios'

interface IHibotData {
    appUUID: String;
    companyUUID: String;
    channelId: String;
    phone: String;
    country: String;
    enable: Boolean;
    number: String;
}

export default class ConversationsConfigService {
    static baseUrl: string = process.env.VUE_APP_CONVERSATIONS_API_URL

    static defaultConversationConfig = {
      authMethod: 'form',
      authOptions: 'none',
      urlBase: '',
      formFields: [],
      linkOfTermsAndConditions: '',
      showTermsAndConditions: false,
      textOfTermsAndConditions: '',
      authTwoFactor: 'onDemand',
      theme: 'light',
      mainColor: '#000000',
      position: 'right',
      logo: '',
      bubbleMessage: '',
      disableMultimedia: false,
      language: 'es',
      welcomeForm: {
        text: '',
        color: '#000000',
        fontSize: 14,
        textAlign: 'left'
      },
      customizedQuestions: [],
      permanentMessages: [],
      finishOptions: [],
      quickResponses: [],
      inactivityMessage: '',
      inactivitySeconds: 0,
      workingHours: {
        message: '',
        customFormTitle: '',
        enable: false,

        blockMessageComposer: false,
        days: [
          {
            code: 0,
            isClosed: false,
            schedules: []
          },
          {
            code: 1,
            isClosed: false,
            schedules: []
          },
          {
            code: 2,
            isClosed: false,
            schedules: []
          },
          {
            code: 3,
            isClosed: false,
            schedules: []
          },
          {
            code: 4,
            isClosed: false,
            schedules: []
          },
          {
            code: 5,
            isClosed: false,
            schedules: []
          },
          {
            code: 6,
            isClosed: false,
            schedules: []
          }
        ]
      },
      timeZone: { label: 'America/Bogota', offset: '−05:00' }
    };

    static async getConversationsConfigV5 () {
      const appId = store.getters['AppStore/getSelectedApp']?.uuid
      const url = `${ConversationsConfigService.baseUrl}/config?appUUID=${appId}`
      const token = store.getters['UserStore/getToken']

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        return new RestResponseFactory().process({ response })
      } catch (e) {
        return new RestResponseFactory().process({
          response: { status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR }
        })
      }
    }

    static async updateConversationsConfigV5 (data) {
      const appId = store.getters['AppStore/getSelectedApp']?.uuid
      const url = `${ConversationsConfigService.baseUrl}/config?appUUID=${appId}`
      const token = store.getters['UserStore/getToken']
      try {
        const response = await axios.put(url, {
          ...data,
          appUUID: appId,
          userId: store.getters['UserStore/getUser']?.uuid || ''
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        return new RestResponseFactory().process({ response })
      } catch (e) {
        return new RestResponseFactory().process({
          response: { status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR }
        })
      }
    }

    // Channels functions
    static async getChannelsV5 () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/company/${companyId}/app/${appId}/configurations/channels`
        const response = await axios.get(url)
        return new RestResponseFactory().process({ response })
      } catch (error) {
        return new RestResponseFactory().process({ response: { status: 500 } })
      }
    }

    static async getChannels () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/config/channels?appUUID=${appId}`
        const response = await axios.get(url)
        return new RestResponseFactory().process({ response })
      } catch (error) {
        return new RestResponseFactory().process({ response: { status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR } })
      }
    }

    static async getAllChannels () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/config/all-channels?appUUID=${appId}`
        const token = store.getters['UserStore/getToken']
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        return new RestResponseFactory().process({ response })
      } catch (error) {
        return new RestResponseFactory().process({ response: { status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR } })
      }
    }

    // Smooch functions
    static async updateSmoochInfo (data) {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${process.env.VUE_APP_V4_CONVERSATIONS_API}configurations/company/${companyId}/app/${appId}/Smooch`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.put(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response
      } catch (error) {
        console.error(error)
      }
    }

    static async createSmoochInfo (data) {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${process.env.VUE_APP_V4_CONVERSATIONS_API}configurations/company/${companyId}/app/${appId}/Smooch`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response
      } catch (error) {
        console.error(error)
      }
    }

    static async getSmoochTemplates () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${process.env.VUE_APP_V4_CONVERSATIONS_API}configurations/company/${companyId}/app/${appId}/templates-smooch`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return { templates: response.data.data }
      } catch (error) {
        console.error(error)
      }
    }

    static async updateTemplate (data) {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${process.env.VUE_APP_V4_CONVERSATIONS_API}configurations/company/${companyId}/app/${appId}/templates-smooch`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return { templates: response.data.data }
      } catch (error) {
        console.error(error)
      }
    }

    // Hibot Functions
    static async createHibotInfo (data: IHibotData) {
      try {
        const url = `${ConversationsConfigService.baseUrl}/hibot`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response
      } catch (error) {
        console.error(error)
      }
    }

    static async updateHibotInfo (data: IHibotData) {
      try {
        const url = `${ConversationsConfigService.baseUrl}/hibot`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.put(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response
      } catch (error) {
        console.error(error)
      }
    }

    static async deleteHibotInfo (hibotId: string) {
      try {
        const url = `${ConversationsConfigService.baseUrl}/hibot/${hibotId}`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.delete(url, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response
      } catch (error) {
        console.error(error)
      }
    }

    static async getHibotInfo () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/hibot/company/${companyId}/app/${appId}`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response.data
      } catch (error) {
        console.error(error)
      }
    }

    // Facebook Functions
    static async createFacebookConfig (data: CreateFacebookConfigInput) {
      try {
        const appUUID = data.appUUID || store.getters['AppStore/getSelectedApp']?.uuid
        const companyUUID = data.companyUUID || store.getters['CompanyStore/getSelectedCompany']?.uuid
        const requestUrl = `${ConversationsConfigService.baseUrl}/configurations/company/${companyUUID}/app/${appUUID}/channels/facebook-messenger`
        const response = await axios.post(requestUrl, {
          pageId: data.pageId,
          pageAccessToken: data.pageAccessToken,
          pageName: data.pageName
        })
        if (response.data?.data?.error) {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    }

    static async updateFacebookConfig (data: UpdateFacebookConfigInput) {
      try {
        const appUUID = data.appUUID || store.getters['AppStore/getSelectedApp']?.uuid
        const companyUUID = data.companyUUID || store.getters['CompanyStore/getSelectedCompany']?.uuid
        const requestUrl = `${ConversationsConfigService.baseUrl}/configurations/company/${companyUUID}/app/${appUUID}/channels/facebook-messenger`
        const response = await axios.put(requestUrl, {
          pageId: data.pageId,
          pageAccessToken: data.pageAccessToken,
          pageName: data.pageName
        })
        if (response.data?.data?.error) {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    }

    // Instagram Functions
    static async createInstagramConfig (data: CreateInstagramConfigInput) {
      try {
        const appUUID = data.appUUID || store.getters['AppStore/getSelectedApp']?.uuid
        const companyUUID = data.companyUUID || store.getters['CompanyStore/getSelectedCompany']?.uuid
        const requestUrl = `${ConversationsConfigService.baseUrl}/configurations/company/${companyUUID}/app/${appUUID}/channels/instagram`
        const response = await axios.post(requestUrl, {
          pageName: data.pageName,
          instagramId: data.instagramId,
          pageId: data.pageId,
          pageAccessToken: data.pageAccessToken
        })
        if (response.data?.data?.error) {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    }

    static async updateInstagramConfig (data: UpdateInstagramConfigInput) {
      try {
        const appUUID = data.appUUID || store.getters['AppStore/getSelectedApp']?.uuid
        const companyUUID = data.companyUUID || store.getters['CompanyStore/getSelectedCompany']?.uuid
        const requestUrl = `${ConversationsConfigService.baseUrl}/configurations/company/${companyUUID}/app/${appUUID}/channels/instagram`
        const response = await axios.put(requestUrl, {
          pageName: data.pageName,
          instagramId: data.instagramId,
          pageId: data.pageId,
          pageAccessToken: data.pageAccessToken
        })
        if (response.data?.data?.error) {
          throw new Error(response.data.message)
        }
      } catch (error) {
        console.error(error)
        throw error
      }
    }

    // Bots Functions
    static async getBotsConfigInfo () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/company/${companyId}/app/${appId}/bot-configuration`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${v4Token}`
          }
        })
        return response.data
      } catch (error) {
        console.error(error)
      }
    }

    static async setBotsConfigInfo (data) {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/company/${companyId}/app/${appId}/bot-configuration/setWatsonBotData`
        const v4Token = store.getters['UserStore/getToken']
        const response = await axios.post(url, data, {
          headers: {
            Authorization: `Bearer ${v4Token}`,
            'Content-Type': 'application/json'
          }
        })
        return response
      } catch (e) {
        console.error(e)
      }
    }

    static async restoreWatsonContext () {
      try {
        const appId = store.getters['AppStore/getSelectedApp']?.uuid
        // const companyId = store.getters['CompanyStore/getSelectedCompany']?.uuid
        // const url = `${ConversationsConfigService.baseUrl}/company/${companyId}/app/${appId}/bot-configuration/watson-context`
        const url = `${ConversationsConfigService.baseUrl}/app/${appId}/bot-configuration/watson-context`
        const token = store.getters['UserStore/getToken']
        const response = await axios.delete(
          url,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        return new RestResponseFactory().process({ response })
      } catch (e) {
        return new RestResponseFactory().process({
          response: {
            status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR,
            message: e.message
          }
        })
      }
    }

    static async saveWhatsappAccessToken (whatsappResponse) {
      try {
        const appUUID = store.getters['AppStore/getSelectedApp']?.uuid
        const companyUUID = store.getters['CompanyStore/getSelectedCompany']?.uuid
        const url = `${ConversationsConfigService.baseUrl}/config/whatsapp`
        const token = store.getters['UserStore/getToken']
        const response = await axios.post(
          url, {
            whatsappResponse: whatsappResponse,
            appUUID,
            companyUUID
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        return new RestResponseFactory().process({ response })
      } catch (error) {
        console.error(error)
      }
    }

    static async getQuickResponses () {
      const appUUID = store.getters['AppStore/getSelectedApp']?.uuid
      const userId = store.getters['UserStore/getUser']?.userId
      const url = `${ConversationsConfigService.baseUrl}/config/quickResponse?appUUID=${appUUID}&userId=${userId}`
      try {
        const token = store.getters['UserStore/getToken']
        const response = await axios.get(
          url,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        return new RestResponseFactory().process({ response })
      } catch (error) {
        return new RestResponseFactory().process({
          response: {
            status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR
          }
        })
      }
    }

    static async saveQuickResponse (data) {
      const appUUID = store.getters['AppStore/getSelectedApp']?.uuid
      const url = `${ConversationsConfigService.baseUrl}/config/quickResponse`
      const { name, value, userId, type, mimetype } = data
      try {
        const token = store.getters['UserStore/getToken']
        const response = await axios.post(
          url, {
            appUUID,
            name,
            value,
            userId,
            type,
            mimetype
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        return new RestResponseFactory().process({ response })
      } catch (error) {
        return new RestResponseFactory().process({
          response: {
            status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR
          }
        })
      }
    }

    static async deleteQuickResponse (data) {
      const url = `${ConversationsConfigService.baseUrl}/config/quickResponse`
      const { _id } = data
      const appUUID = store.getters['AppStore/getSelectedApp']?.uuid
      try {
        const token = store.getters['UserStore/getToken']
        const response = await fetch(
          url, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              quickResponseId: _id,
              appUUID
            })
          })
        const data = await response.json()
        console.log(data)
        return new RestResponseFactory().process({ response: { ...data, status: data.statusCode } })
      } catch (error) {
        return new RestResponseFactory().process({
          response: {
            status: StatusCodes.HTTP_500_INTERNAL_SERVER_ERROR
          }
        })
      }
    }
}
